import { Injectable, Inject, InjectionToken } from '@angular/core';
import { SentryService } from './sentry.service';

/**
 * Injection token for environment configuration
 */
export const ENVIRONMENT = new InjectionToken<{ production: boolean }>(
  'environment',
);

/**
 * Logging levels
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

/**
 * Secure logging service that prevents sensitive information leakage in production.
 *
 * In development mode, logs to console.
 * In production mode, only errors are logged and sent to Sentry.
 *
 * @example
 * ```typescript
 * constructor(private logger: LoggerService) {}
 *
 * this.logger.debug('User data loaded', { userId: 123 });
 * this.logger.error('Failed to save', error);
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private currentLevel: LogLevel;

  constructor(
    @Inject(ENVIRONMENT) private readonly env: { production: boolean },
    private readonly sentry: SentryService,
  ) {
    // In production, only log errors. In dev, log everything.
    this.currentLevel = env.production ? LogLevel.ERROR : LogLevel.DEBUG;
  }

  /**
   * Private helper to safely log to console (dev only)
   */
  private logToConsole(
    level: string,
    message: string,
    context?: unknown,
  ): void {
    if (this.env.production || typeof console === 'undefined') {
      return;
    }
    // eslint-disable-next-line no-console
    console.log(`[${level}] ${message}`, context !== undefined ? context : '');
  }

  /**
   * Log debug information (development only)
   */
  debug(message: string, context?: unknown): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.logToConsole('DEBUG', message, context);
    }
  }

  /**
   * Log informational messages (development only)
   */
  info(message: string, context?: unknown): void {
    if (this.shouldLog(LogLevel.INFO)) {
      this.logToConsole('INFO', message, context);
    }
  }

  /**
   * Log warning messages
   */
  warn(message: string, context?: unknown): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`[WARN] ${message}`, context !== undefined ? context : '');
    }
  }

  /**
   * Log error messages (always logged, sent to Sentry in production)
   */
  error(message: string, error?: Error | unknown, context?: unknown): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      if (this.env.production) {
        // In production, log generic error without sensitive details
        console.error(`[ERROR] ${message}`);

        // Send to Sentry for monitoring
        this.sendToMonitoring(message, error, context);
      } else {
        // In development, log full error details
        console.error(`[ERROR] ${message}`, {
          error,
          context,
          timestamp: new Date().toISOString(),
        });
      }
    }
  }

  /**
   * Set log level programmatically (useful for testing)
   */
  setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }

  /**
   * Check if message should be logged based on current level
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= this.currentLevel;
  }

  /**
   * Send errors to Sentry for monitoring and alerting
   */
  private sendToMonitoring(
    message: string,
    error?: Error | unknown,
    context?: unknown,
  ): void {
    if (this.env.production) {
      if (error instanceof Error) {
        this.sentry.captureException(error, {
          extra: {
            message,
            context,
          },
        });
      } else {
        this.sentry.captureMessage(message, {
          level: 'error',
          extra: {
            error,
            context,
          },
        });
      }
    }
  }
}
